import { HOUSE_PALETTES, MAX_SPEED, BASE_SPEED, SPEED_PER_HOUSE, JUMP_V, GRAVITY } from "./constants";
import { chance, mulberry32, pick, range } from "./rng";
import type { House, Obstacle, ObstacleKind } from "./types";

export function runSpeed(housesLit: number): number {
  return Math.min(MAX_SPEED, BASE_SPEED + housesLit * SPEED_PER_HOUSE);
}

export function maxJumpDistance(speed: number): number {
  const hang = (2 * JUMP_V) / GRAVITY;
  return speed * hang * 0.84;
}

export class Neighborhood {
  houses: House[] = [];
  private nextId = 1;
  private cursor = 40;
  private rand: () => number;

  constructor(seed = Date.now()) {
    this.rand = mulberry32(seed >>> 0);
    this.spawnTutorial();
    while (this.cursor < 2800) this.spawn(0);
  }

  houseById(id: number): House | undefined {
    return this.houses.find((h) => h.id === id);
  }

  prune(cameraX: number): void {
    this.houses = this.houses.filter((h) => h.x + h.w > cameraX - 400);
  }

  ensureAhead(cameraX: number, viewW: number, housesLit: number): void {
    while (this.cursor < cameraX + viewW + 1400) this.spawn(housesLit);
  }

  private spawnTutorial(): void {
    this.pushHouse({
      w: 1500,
      roofY: 448,
      stories: 1,
      gap: 0,
      obstacles: [],
      housesLit: 0,
    });
    this.pushHouse({
      w: 300,
      roofY: 454,
      stories: 1,
      gap: 56,
      obstacles: [],
      housesLit: 0,
    });
    this.pushHouse({
      w: 270,
      roofY: 438,
      stories: 1,
      gap: 64,
      obstacles: [],
      housesLit: 0,
    });
  }

  private spawn(housesLit: number): void {
    const speed = runSpeed(housesLit);
    const jumpable = maxJumpDistance(speed);
    const difficulty = Math.min(1, housesLit / 28);
    const minGap = 70 + difficulty * 24;
    const maxGap = Math.min(jumpable * 0.78, 70 + difficulty * 150);
    const gap = housesLit === 0 ? range(this.rand, 80, 110) : range(this.rand, minGap, Math.max(minGap + 8, maxGap));
    const w = range(this.rand, 200, 320 - difficulty * 40);
    const roofY = range(this.rand, 390, 500);
    const stories: 1 | 2 = chance(this.rand, 0.45 + difficulty * 0.2) ? 2 : 1;
    const obstacles = this.pickObstacles(w, housesLit, difficulty);
    this.pushHouse({ w, roofY, stories, gap, obstacles, housesLit });
  }

  private pickObstacles(w: number, housesLit: number, difficulty: number): Obstacle[] {
    if (housesLit < 3) return [];
    const out: Obstacle[] = [];
    const roll = this.rand();
    const p = 0.18 + difficulty * 0.55;
    if (roll > p) return out;

    const kinds: ObstacleKind[] = ["chimney"];
    if (housesLit >= 5) kinds.push("ice", "squirrel");
    if (housesLit >= 8) kinds.push("inflatable");

    const kind = pick(this.rand, kinds);
    if (kind === "chimney") {
      out.push({
        kind,
        x: range(this.rand, w * 0.28, w * 0.62),
        w: 28,
        clipped: false,
        phase: this.rand(),
      });
    } else if (kind === "ice") {
      out.push({
        kind,
        x: range(this.rand, w * 0.2, w * 0.55),
        w: range(this.rand, 50, 80),
        clipped: false,
        phase: this.rand(),
      });
    } else if (kind === "squirrel") {
      out.push({
        kind,
        x: range(this.rand, 20, w * 0.7),
        w: 22,
        clipped: false,
        phase: this.rand() * Math.PI * 2,
      });
    } else {
      out.push({
        kind,
        x: 8,
        w: Math.min(86, w * 0.34),
        clipped: false,
        phase: this.rand(),
      });
    }
    if (housesLit >= 16 && chance(this.rand, 0.28) && kind !== "inflatable") {
      out.push({
        kind: "chimney",
        x: Math.min(w - 40, out[0]!.x + 90),
        w: 26,
        clipped: false,
        phase: this.rand(),
      });
    }
    return out;
  }

  private pushHouse(opts: {
    w: number;
    roofY: number;
    stories: 1 | 2;
    gap: number;
    obstacles: Obstacle[];
    housesLit: number;
  }): void {
    if (opts.gap > 0) this.cursor += opts.gap;
    const seed = Math.floor(this.rand() * 1e9);
    const house: House = {
      id: this.nextId++,
      x: this.cursor,
      w: opts.w,
      roofY: opts.roofY,
      roofH: 42 + (opts.stories === 2 ? 8 : 0),
      eave: 10,
      stories: opts.stories,
      palette: Math.floor(this.rand() * HOUSE_PALETTES.length),
      lightStyle: chance(this.rand, 0.55) ? "warm" : "multi",
      hasColumns: chance(this.rand, 0.55),
      hasTree: chance(this.rand, 0.7),
      hasWreath: chance(this.rand, 0.6),
      lit: false,
      lightT: 0,
      obstacles: opts.obstacles,
      seed,
    };
    this.houses.push(house);
    this.cursor += opts.w;
  }
}

export function roofBounds(h: House): { left: number; right: number; y: number } {
  return {
    left: h.x + 8,
    right: h.x + h.w - 8,
    y: h.roofY,
  };
}

export function obstacleWorldX(h: House, o: Obstacle, now: number): number {
  if (o.kind === "squirrel") {
    const t = now * 1.6 + o.phase;
    const travel = Math.max(20, h.w - 50);
    return h.x + 16 + (Math.sin(t) * 0.5 + 0.5) * travel;
  }
  return h.x + o.x;
}
